import { useEffect, useRef, useState } from 'react';
import { zigzagHandler } from '@croquiscom/web2app';
import { useAtomValue, useUpdateAtom } from 'jotai/utils';
import { useRouter } from 'next/router';

import { ZigzagEvent } from '@common/app-manager';
import {
  change_shop_bookmark_atom,
  shop_bookmark_atom,
  useCatalogProductIdParams,
  usePdpBaseInfo,
} from '@domains/pdp-common';
import { isPreview } from '@domains/pdp-common';

import { useSendSellerZonLog } from '../log/useSendSellerZonLog';

export interface BookmarkStatus {
  isBookmarked: boolean;
  bookmarkCount: number;
}

export const useBookmarkService = () => {
  const { asPath } = useRouter();
  const is_preview = isPreview(asPath);
  const { saveShopLog, removeSavedShopLog } = useSendSellerZonLog();

  const params = useCatalogProductIdParams();
  const { product, shop, brand_home } = usePdpBaseInfo(params);

  const bookmark_item = brand_home ?? shop;

  const initial_bookmark_status = useRef<BookmarkStatus>({
    isBookmarked: false,
    bookmarkCount: 0,
  });
  const [bookmark_status, setBookmarkStatus] = useState<BookmarkStatus>({ isBookmarked: false, bookmarkCount: 0 });
  const setShopBookmark = useUpdateAtom(shop_bookmark_atom);
  const change_shop_bookmark = useAtomValue(change_shop_bookmark_atom);

  const is_visible_bookmark_count = !brand_home;

  const setBookmark = async (e: React.MouseEvent) => {
    e.stopPropagation();
    let isBookmarked;
    if (bookmark_status.isBookmarked) {
      isBookmarked = await ZigzagEvent.rbk(
        bookmark_item.main_domain,
        removeSavedShopLog(product.id, shop.id, brand_home?.id),
      );
    } else {
      isBookmarked = await ZigzagEvent.abk(bookmark_item.main_domain, saveShopLog(product.id, shop.id, brand_home?.id));
    }
    toggleBookmarkStatus(isBookmarked);
  };

  const toggleBookmarkStatus = (isBookmarked: boolean) => {
    const initial = initial_bookmark_status.current;
    const bookmarkCount = initial.bookmarkCount + (initial.isBookmarked ? -1 : +1);
    if (isBookmarked === initial.isBookmarked) {
      setBookmarkStatus(initial);
    } else {
      setBookmarkStatus({ isBookmarked, bookmarkCount });
    }
    setShopBookmark(isBookmarked);
  };

  useEffect(() => {
    const isBookmarked = brand_home ? brand_home.is_saved_shop : shop.is_saved_shop;
    initial_bookmark_status.current.isBookmarked = isBookmarked;
    initial_bookmark_status.current.bookmarkCount = shop.bookmark_count ?? 0;
    toggleBookmarkStatus(isBookmarked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop.is_saved_shop, shop.bookmark_count, brand_home]);

  useEffect(() => {
    if (change_shop_bookmark) {
      toggleBookmarkStatus(change_shop_bookmark);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [change_shop_bookmark]);

  useEffect(() => {
    try {
      zigzagHandler('onBookmarkStatus', (maps) => {
        if (bookmark_item.main_domain in maps) {
          toggleBookmarkStatus(maps[bookmark_item.main_domain]);
        }
      });
    } catch (err) {
      console.error(err);
    }
  }, [bookmark_item.main_domain, toggleBookmarkStatus]);

  return {
    shop,
    brand_home,
    is_preview,
    bookmark_item,
    is_visible_bookmark_count,
    bookmark_status,
    setBookmark,
  };
};
